import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const REVIEWS = [
  {
    name: "Houssam M.",
    when: "il y a 2 mois",
    rating: 5,
    text: "Excellent service ! Intervention rapide après mon appel, technicien très compétent et à l'écoute. Le travail a été fait proprement et avec sérieux. Tarif correct pour la qualité. Je recommande sans hésiter.",
  },
  {
    name: "Marion Jaegle",
    when: "il y a 6 jours",
    rating: 5,
    text: "Superbe équipe qui a réalisé mon déménagement. Très à l'écoute, disponible et réactif. Équipe impliquée dans leur travail, qui a su réaliser mon déménagement avec sérieux.",
  },
  {
    name: "Sabine Boutonnet",
    when: "il y a une semaine",
    rating: 5,
    text: "Très bonne expérience, équipe très dynamique et disponible ; beaucoup d'empathie et d'implication !! Je recommande vivement !",
  },
  {
    name: "Camille Sollers",
    when: "il y a 2 mois",
    rating: 5,
    text: "Merci encore pour votre efficacité. Si vous devez déménager un objet encombrant, contactez-les !!",
  },
  {
    name: "Samira Ehrhart",
    when: "il y a 2 mois",
    rating: 5,
    text: "Super équipe, très professionnelle. Je recommande fortement.",
  },
  {
    name: "Noëlle Seichepine",
    when: "il y a une semaine",
    rating: 5,
    text: "Personne très sérieuse et sympathique. Je recommande ★★★★★",
  },
  {
    name: "Pierre Vieroli",
    when: "il y a une semaine",
    rating: 5,
    text: "Merci pour le dépannage, personne super aimable 👌",
  },
];

const GOOGLE_REVIEW_URL = "https://maps.app.goo.gl/gT2azA9MvcBAzM1dA";

export default function Reviews() {
  return (
    <section
      id="avis"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 bg-white border-y border-black/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-20">
          <div className="md:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-5">
              05 · Ils parlent de nous
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter leading-[1.02]">
              Vos retours,<br />
              <span className="text-[#FF2800]">notre fierté.</span>
            </h2>
          </div>

          <div className="md:col-span-6 flex md:justify-end items-end">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-rating-card"
              className="group inline-flex items-center gap-6 bg-[#FAFAFA] hover:bg-black hover:text-white border border-black/10 rounded-2xl px-8 py-6 transition-colors duration-300"
            >
              <div>
                <div className="font-display text-5xl md:text-6xl font-light tracking-tighter">
                  4<span className="text-[#FF2800]">,</span>9
                </div>
                <div className="flex items-center gap-1 mt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} className="fill-[#FFB800] text-[#FFB800]" />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 group-hover:text-neutral-300">
                  Google Reviews
                </div>
                <div className="text-sm font-medium mt-1">
                  D.E.S Dépannage Strasbourg
                </div>
                <div className="text-xs text-neutral-500 group-hover:text-neutral-400 mt-1">
                  Voir tous les avis →
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Marquee row */}
        <div className="relative">
          <div className="flex gap-6 md:gap-8 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth -mx-6 md:-mx-12 px-6 md:px-12 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {REVIEWS.map((r, i) => (
              <ReviewCard key={i} review={r} index={i} />
            ))}
          </div>
          {/* Fade edges */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent" />
        </div>

        <div className="mt-14 text-center">
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-cta"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold bg-black text-white hover:bg-[#FF2800] transition"
          >
            <GoogleLogo />
            Laisser un avis sur Google
          </a>
        </div>
      </div>
    </section>
  );
}

function ReviewCard({ review, index }) {
  return (
    <motion.article
      data-testid={`review-card-${index}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.06 }}
      className="snap-start shrink-0 w-[88%] sm:w-[55%] md:w-[36%] lg:w-[28%] bg-[#FAFAFA] border border-black/10 rounded-2xl p-8 hover:bg-white hover:border-black/20 transition-all duration-300"
    >
      <Quote size={24} className="text-[#FF2800] mb-5" strokeWidth={1.5} />
      <div className="flex items-center gap-1 mb-4">
        {Array.from({ length: review.rating }).map((_, i) => (
          <Star key={i} size={14} className="fill-[#FFB800] text-[#FFB800]" />
        ))}
      </div>
      <p className="text-neutral-700 leading-relaxed text-[15px] mb-6 min-h-[7em]">
        « {review.text} »
      </p>
      <div className="flex items-center justify-between border-t border-black/10 pt-5">
        <div>
          <div className="font-display font-medium tracking-tight text-black">{review.name}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{review.when}</div>
        </div>
        <GoogleLogo />
      </div>
    </motion.article>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-label="Google" className="shrink-0">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.09A6.96 6.96 0 0 1 5.47 12c0-.73.13-1.44.36-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
  );
}
